import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { applyNodeChanges, applyEdgeChanges, Position } from '@xyflow/react';
import { appendNodeIo, createNode, deleteNodeIo, patchNode, patchNodeParams, removeNode } from '@/api/nodes';
import { disconnectInputFromOutput, connectInputToOutput, duplicateNodes, getPathway } from '@/api/pathways';
import {
  parseNodes,
  parseEdges,
  getLayoutedNodes,
  parseNode,
  getNodesById,
  serializeEdge,
  serializeHandle,
  buildHandle,
  applyNodeInfo,
} from '@/utils/pathwayUtils';
import useCaseStore, { caseActions } from './caseStore';
import { keyBy } from '@/utils/miscUtils';
import { pathwaysCacheActions } from './pathwaysCacheStore';
import { isCombinerNodeName, isSplitterNodeName, NODE_TYPES } from '@/consts';
import { getCase, patchPositions, updateEdgeControlPoint } from '@/api/cases';
import { calculateSuggestedConnections } from '@/utils/suggestionUtils';
import { toast } from 'react-toastify';

const SUGGESTION_TIMEOUT_MS = 8000;

const initialValues = {
  nodes: [],
  edges: [],
  suggestedEdges: [],
  analysis_id: null,
};

const usePathwayStore = create()(immer(() => initialValues));
const { setState: set, getState: get } = usePathwayStore;

const isIoConnected = (io, nodeId, handleType, edges) => {
  const handle = buildHandle(nodeId, handleType, io.name);

  return edges.some(edge => (handleType === 'output' ? edge.sourceHandle === handle : edge.targetHandle === handle));
};

const getResponsePathway = data => {
  if (data?.nodes && data?.connections) return data;
  if (data?.pathway?.nodes && data?.pathway?.connections) return data.pathway;
};

const getResponseNode = data => {
  if (data?.id && data?.inputs && data?.outputs) return data;
  if (data?.node?.id && data?.node?.inputs && data?.node?.outputs) return data.node;
};

const getNodeIoConfigByName = name => {
  if (isSplitterNodeName(name)) {
    return { key: 'outputs', handleType: 'output', handleKey: 'sourceHandle' };
  }

  if (isCombinerNodeName(name)) {
    return { key: 'inputs', handleType: 'input', handleKey: 'targetHandle' };
  }
};

const getIoNameRemap = (oldIos, newIos) => {
  return oldIos.reduce((acc, oldIo, index) => {
    const newIo = newIos[index];

    if (newIo && oldIo.name !== newIo.name) {
      acc[oldIo.name] = newIo;
    }

    return acc;
  }, {});
};

const replaceNodeData = serverNode => {
  set(state => {
    const node = state.nodes.find(node => node.id === serverNode.id);

    if (!node) return;

    const config = getNodeIoConfigByName(serverNode.name);
    const ioRemap = config ? getIoNameRemap(node.data?.[config.key] ?? [], serverNode[config.key] ?? []) : {};

    node.type = parseNode(serverNode).type;
    node.data = {
      ...node.data,
      ...serverNode,
      id: serverNode.id,
      label: node.data.label ?? serverNode.label,
    };

    if (config && Object.keys(ioRemap).length > 0) {
      state.edges.forEach(edge => {
        if (config.handleType === 'output' && edge.source !== serverNode.id) return;
        if (config.handleType === 'input' && edge.target !== serverNode.id) return;

        const [handleNodeId, oldIoName] = serializeHandle(edge[config.handleKey], config.handleType);
        const newIo = ioRemap[oldIoName];

        if (handleNodeId !== serverNode.id || !newIo) return;

        edge[config.handleKey] = buildHandle(serverNode.id, config.handleType, newIo.name);
        edge.id = `${edge.sourceHandle}-${edge.targetHandle}`;

        if (config.handleType === 'output') {
          edge.name = newIo.name;
          edge.label = newIo.type?.label ?? newIo.label ?? edge.label;
        }

        edge.data = {
          ...edge.data,
          name: config.handleType === 'output' ? newIo.name : edge.data?.name,
          anchorable: newIo.anchorable ?? edge.data?.anchorable,
          unit: newIo.type?.unit ?? edge.data?.unit,
          typeName: newIo.type?.name ?? edge.data?.typeName,
        };
      });

      state.edgesBySource = keyBy(state.edges, 'sourceHandle');
      state.edgesByTarget = keyBy(state.edges, 'targetHandle');
    }
  });
};

const applyIoResponse = data => {
  const pathway = getResponsePathway(data);

  if (pathway) {
    pathwayActions.update(pathway);
    return;
  }

  const node = getResponseNode(data);

  if (node) {
    replaceNodeData(node);
    caseActions.markUnbalanced();
  }
};

const getDynamicNodeIo = (nodeId, ioName) => {
  const { id: pathwayId, nodes, edges } = get();
  const node = nodes.find(node => node.id === nodeId);
  const config = getNodeIoConfigByName(node?.data?.name);

  if (!node || !config) return {};

  const ios = node.data?.[config.key] ?? [];
  const io = ioName ? ios.find(io => io.name === ioName) : ios.at(-1);

  return {
    pathwayId,
    node,
    config,
    ios,
    io,
    isConnected: io ? isIoConnected(io, node.id, config.handleType, edges) : false,
  };
};

// actions
export const pathwayActions = {
  init: async (pathway, caseData) => {
    const {
      id,
      label,
      case_id,
      name,
      connections,
      nodes,
      locked,
      custom_properties: customProperties = [],
      parent_case_id,
      parent_node_id,
      parent_pathway_id,
    } = pathway;
    const nodesById = getNodesById(nodes);
    const edges = parseEdges(connections, nodesById, caseData?.edge_info);
    const nodeInfo = caseData?.node_info ?? useCaseStore.getState().node_info;
    let nodesWithPositions = [];

    const positionsPresent = !!Object.values(nodeInfo ?? {})?.[0]?.position;
    const initialNodes = parseNodes(applyNodeInfo(nodes, nodeInfo));
    const cachedNodes = pathwaysCacheActions.getCachedNodePositions(id);

    nodesWithPositions = await getLayoutedNodes(initialNodes, cachedNodes, edges);

    if (!positionsPresent) {
      pathwayActions.syncPositions(caseData.id, nodesWithPositions);
    } else {
      nodesWithPositions = nodesWithPositions.map(node => ({
        ...node,
        position: nodeInfo?.[node.id]?.position || node.position,
      }));
    }

    const cachedMirrors = pathwaysCacheActions.getCachedNodeMirrors(id);
    if (cachedMirrors) {
      nodesWithPositions = nodesWithPositions.map(node => {
        if (!(node.id in cachedMirrors)) return node;
        const isMirrored = cachedMirrors[node.id];
        return {
          ...node,
          data: { ...node.data, mirrored: isMirrored },
          sourcePosition: isMirrored ? Position.Left : Position.Right,
          targetPosition: isMirrored ? Position.Right : Position.Left,
        };
      });
    }

    const edgesBySource = keyBy(edges, 'sourceHandle');
    const edgesByTarget = keyBy(edges, 'targetHandle');

    set({
      id,
      case_id,
      parent_case_id,
      parent_node_id,
      parent_pathway_id,
      label,
      name,
      nodes: nodesWithPositions,
      edges,
      edgesBySource,
      edgesByTarget,
      locked,
      customProperties,
    });
  },

  syncPositions: async (caseId, nodesWithPositions) => {
    const nodePositions = nodesWithPositions.reduce((acc, node) => {
      acc[node.id] = { position: node.position };
      return acc;
    }, {});

    await patchPositions(caseId, nodePositions);
  },
  clear: () => set(initialValues, true),
  setCustomProperties: customProperties => {
    set({ customProperties });
    caseActions.recheckBalanceAndAnalysis();
  },
  onNodesChange: changes => {
    const change = changes?.[0];

    if (change?.type === 'position' && change?.position && change?.dragging !== true) {
      const pathwayId = get().id;
      const {
        id: nodeId,
        position: { x, y },
      } = change;

      pathwaysCacheActions.cacheNodePosition(pathwayId, nodeId, { x, y });
    }

    const nodes = JSON.parse(JSON.stringify(get().nodes));
    set({ nodes: applyNodeChanges(changes, nodes) });
  },

  onNodeDragStop: async (event, data) => {
    const { case_id: caseId } = get();
    const { id, position } = data;

    await patchNode(caseId, id, { position });
  },
  onEdgesChange: changes => {
    set({ edges: applyEdgeChanges(changes, get().edges) });
  },
  updateEdgeControlPoint: (edgeId, controlPoint) => {
    set(state => {
      const edge = state.edges.find(edge => edge.id === edgeId);

      if (!edge) return;

      edge.data = {
        ...edge.data,
        controlPoint,
      };
    });
  },
  saveEdgeControlPoint: async edgeId => {
    const { case_id: caseId, edges } = get();
    const edge = edges.find(edge => edge.id === edgeId);
    const controlPoint = edge?.data?.controlPoint;

    if (!edge || !controlPoint) return;

    try {
      await updateEdgeControlPoint(caseId, {
        ...serializeEdge(edge),
        control_point: { x: controlPoint.x, y: controlPoint.y },
      });
    } catch (error) {
      toast.error('Failed to save stream position');
      console.error('Save edge control point error:', error);
    }
  },
  addNode: async (nodeTypeStr, position) => {
    const { id: pathwayId, case_id: caseId } = get();
    const nodeType = JSON.parse(nodeTypeStr);
    const { name, type } = nodeType;
    const { data } = await createNode(pathwayId, { name });

    const label = nodeType.label || data.label;

    if (type === NODE_TYPES.system) {
      const systemId = nodeType?.data?.system?.id;
      await patchNodeParams(caseId, data.id, { params: { system_id: { primary: { value: systemId } } } });
    }

    pathwaysCacheActions.cacheNodePosition(pathwayId, data.id, position);

    const node = parseNode({ ...data, position, name, type, label, data: nodeType?.data });
    await patchNode(caseId, node.id, { position });

    set(state => {
      state.nodes.push(node);
    });

    caseActions.markUnbalanced();

    const [{ data: freshPathway }, { data: freshCase }] = await Promise.all([getPathway(pathwayId), getCase(caseId)]);
    await pathwayActions.init(freshPathway, freshCase);

    const { nodes: allNodes, edges: existingEdges, suggestedEdges: existingSuggestedEdges } = get();

    // Filter out suggestions that reference nodes that no longer exist
    const validNodeIds = new Set(allNodes.map(n => n.id));
    const existingSuggestions = existingSuggestedEdges
      .filter(edge => validNodeIds.has(edge.source) && validNodeIds.has(edge.target))
      .map(edge => ({
        from_node: edge.source,
        from_io: serializeHandle(edge.sourceHandle, 'output')[1],
        to_node: edge.target,
        to_io: serializeHandle(edge.targetHandle, 'input')[1],
      }));

    const suggestions = calculateSuggestedConnections(allNodes, node.id, existingEdges, existingSuggestions);

    if (!suggestions || suggestions.length === 0) {
      return;
    }

    const topSuggestions = suggestions.slice(0, 1);
    const newSuggestions = topSuggestions
      .filter(suggestion => {
        // Validate: from_io must be an output, to_io must be an input
        // This prevents input-to-input or output-to-output connections
        const fromNode = allNodes.find(n => n.id === suggestion.from_node);
        const toNode = allNodes.find(n => n.id === suggestion.to_node);
        const fromIsOutput = fromNode?.data?.outputs?.some(o => o.name === suggestion.from_io);
        const toIsInput = toNode?.data?.inputs?.some(i => i.name === suggestion.to_io);
        return fromIsOutput && toIsInput;
      })
      .map(suggestion => ({
        id: `sugg-${suggestion.from_node}-${suggestion.from_io}-${suggestion.to_node}-${suggestion.to_io}`,
        type: 'suggestedEdge',
        source: suggestion.from_node,
        sourceHandle: buildHandle(suggestion.from_node, 'output', suggestion.from_io),
        target: suggestion.to_node,
        targetHandle: buildHandle(suggestion.to_node, 'input', suggestion.to_io),
        style: { stroke: '#22c55e', strokeDasharray: '5 5', strokeWidth: 3 },
        animated: suggestion.score === 1,
        data: { suggested: true, score: suggestion.score },
        interactionWidth: 20,
      }));

    set(state => {
      state.suggestedEdges.push(...newSuggestions);
    });

    const suggestionIds = newSuggestions.map(suggestion => suggestion.id);
    setTimeout(() => pathwayActions.dismissSuggestions(suggestionIds), SUGGESTION_TIMEOUT_MS);
  },
  dismissSuggestions: ids => {
    const idSet = new Set(ids);
    set(state => {
      state.suggestedEdges = state.suggestedEdges.filter(edge => !idSet.has(edge.id));
    });
  },
  update: pathway => {
    const { edge_info } = useCaseStore.getState();
    const nodesById = getNodesById(pathway.nodes);
    const edges = parseEdges(pathway.connections, nodesById, edge_info);
    const edgesBySource = keyBy(edges, 'sourceHandle');
    const edgesByTarget = keyBy(edges, 'targetHandle');

    set(state => {
      state.nodes.forEach(node => {
        const serverNode = nodesById[node.id];

        if (!serverNode) return;

        node.type = parseNode(serverNode).type;
        node.data.name = serverNode.name;
        node.data.inputs = serverNode.inputs;
        node.data.outputs = serverNode.outputs;
      });
      state.edges = edges;
      state.edgesBySource = edgesBySource;
      state.edgesByTarget = edgesByTarget;
    });

    caseActions.markUnbalanced();
  },
  disconnectInputFromOutput: async edge => {
    const { id: pathwayId } = get();
    const serializedEdge = serializeEdge(edge);
    const { data: pathway } = await disconnectInputFromOutput(pathwayId, serializedEdge);

    // Drop the saved control point so a recreated connection starts at the default
    // position instead of inheriting the stale one keyed by the same endpoints.
    caseActions.removeEdgeInfo(serializedEdge);

    pathwayActions.update(pathway);
  },
  connectInputToOutput: async edge => {
    const { id: pathwayId } = get();

    const serializedEdge = serializeEdge(edge);
    const { data: pathway } = await connectInputToOutput(pathwayId, serializedEdge);

    pathwayActions.update(pathway);

    set(state => {
      // Once a node gets wired by the user, suggestions pointing at it are redundant
      const connectedNodes = new Set([edge.source, edge.target]);
      state.suggestedEdges = state.suggestedEdges.filter(
        suggEdge => !connectedNodes.has(suggEdge.source) && !connectedNodes.has(suggEdge.target),
      );
    });
  },
  addNodeIo: async nodeId => {
    const { pathwayId, node, config } = getDynamicNodeIo(nodeId);

    if (!pathwayId || !node || !config) return;

    try {
      const { data } = await appendNodeIo(pathwayId, node.id, { io_type: config.handleType });
      applyIoResponse(data);
    } catch (error) {
      toast.error('Failed to add node input/output');
      console.error('Append node IO error:', error);
    }
  },
  removeNodeIo: async (nodeId, ioName) => {
    const { pathwayId, node, config, ios, io, isConnected } = getDynamicNodeIo(nodeId, ioName);

    if (!pathwayId || !node || !config || !io || ios.length <= 2 || isConnected) return;

    try {
      const { data } = await deleteNodeIo(pathwayId, node.id, { io_type: config.handleType, io_name: io.name });
      applyIoResponse(data);
    } catch (error) {
      toast.error('Failed to remove node input/output');
      console.error('Delete node IO error:', error);
    }
  },
  pasteNodes: async (clipboardData, targetPosition) => {
    const { id: pathwayId, nodes: existingNodes } = get();
    const { nodes: clipNodes } = clipboardData;

    // Compute centroid of the copied nodes (used for both positioning strategies)
    const centroid = clipNodes.reduce(
      (acc, n) => ({ x: acc.x + n.position.x / clipNodes.length, y: acc.y + n.position.y / clipNodes.length }),
      { x: 0, y: 0 },
    );

    let offsetX, offsetY;

    if (targetPosition) {
      // User clicked on the canvas — center the group on that point
      offsetX = targetPosition.x - centroid.x;
      offsetY = targetPosition.y - centroid.y;
    } else {
      // No click target — find empty area below all existing nodes
      const NODE_HEIGHT = 400;
      const PADDING = 60;

      if (existingNodes.length > 0) {
        const maxY = Math.max(...existingNodes.map(n => n.position.y));
        const targetY = maxY + NODE_HEIGHT + PADDING;
        offsetX = 0;
        offsetY = targetY - centroid.y;
      } else {
        offsetX = 0;
        offsetY = 0;
      }
    }

    // Phase 1: Optimistically add placeholder nodes to the canvas
    const nodesPayload = clipNodes.map(clipNode => {
      const newPosition = {
        x: clipNode.position.x + offsetX,
        y: clipNode.position.y + offsetY,
      };
      return { id: clipNode.originalId, position: newPosition };
    });

    const placeholderNodes = clipNodes.map(clipNode => {
      const newPosition = {
        x: clipNode.position.x + offsetX,
        y: clipNode.position.y + offsetY,
      };
      return parseNode({
        ...clipNode.data,
        id: `paste-placeholder-${clipNode.originalId}`,
        position: newPosition,
        name: clipNode.name,
        type: clipNode.type,
        label: clipNode.label,
      });
    });

    set(state => {
      state.nodes.push(...placeholderNodes);
    });

    // Record existing node IDs (excluding placeholders) so we can identify new ones later
    const existingNodeIds = new Set(
      get()
        .nodes.filter(n => !n.id.startsWith('paste-placeholder-'))
        .map(n => n.id),
    );

    // Build a map from node name to intended positions (queue-based for duplicate names)
    const positionsByName = {};
    clipNodes.forEach(clipNode => {
      const pos = { x: clipNode.position.x + offsetX, y: clipNode.position.y + offsetY };
      if (!positionsByName[clipNode.name]) positionsByName[clipNode.name] = [];
      positionsByName[clipNode.name].push(pos);
    });

    try {
      // Phase 2: Call the duplicate_nodes API with original node IDs and positions
      const { data: freshPathway } = await duplicateNodes(pathwayId, { nodes: nodesPayload });

      // Cache intended positions for newly created nodes before rebuilding state
      const newServerNodes = freshPathway.nodes.filter(n => !existingNodeIds.has(n.id));
      newServerNodes.forEach(newNode => {
        const positions = positionsByName[newNode.name];
        if (positions && positions.length > 0) {
          const pos = positions.shift();
          pathwaysCacheActions.cacheNodePosition(pathwayId, newNode.id, pos);
        }
      });

      // Phase 3: Rebuild local state from server response (same as init)
      const { node_info, edge_info } = useCaseStore.getState();
      const nodesById = getNodesById(freshPathway.nodes);
      const edges = parseEdges(freshPathway.connections, nodesById, edge_info);
      const freshNodes = parseNodes(applyNodeInfo(freshPathway.nodes, node_info));
      const cachedNodes = pathwaysCacheActions.getCachedNodePositions(pathwayId);
      const nodesWithPositions = await getLayoutedNodes(freshNodes, cachedNodes, edges);

      const edgesBySource = keyBy(edges, 'sourceHandle');
      const edgesByTarget = keyBy(edges, 'targetHandle');

      set({
        nodes: nodesWithPositions,
        edges,
        edgesBySource,
        edgesByTarget,
        suggestedEdges: [],
      });

      // Persist positions for the newly created nodes so they survive a refresh
      const { case_id: caseId } = get();
      pathwayActions.syncPositions(caseId, nodesWithPositions);

      caseActions.markUnbalanced();
      toast.success(`Pasted ${clipNodes.length} node(s)`);
    } catch (error) {
      // Remove placeholder nodes on failure
      set(state => {
        const placeholderIds = new Set(placeholderNodes.map(n => n.id));
        state.nodes = state.nodes.filter(n => !placeholderIds.has(n.id));
      });
      toast.error('Failed to paste nodes');
      console.error('Paste error:', error);
    }
  },
  mirrorNode: nodeId => {
    const { id: pathwayId, nodes } = get();
    const node = nodes.find(n => n.id === nodeId);
    const newMirrored = !(node?.data?.mirrored ?? false);

    set(state => {
      const n = state.nodes.find(n => n.id === nodeId);
      if (n) {
        n.data.mirrored = newMirrored;
        n.sourcePosition = newMirrored ? Position.Left : Position.Right;
        n.targetPosition = newMirrored ? Position.Right : Position.Left;
      }
    });

    pathwaysCacheActions.cacheNodeMirror(pathwayId, nodeId, newMirrored);
  },
  removeNode: async nodeId => {
    const { id, suggestedEdges } = get();

    await removeNode(id, nodeId);

    const { edges } = get();

    const updatedEdges = edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId);
    const edgesBySource = keyBy(updatedEdges, 'sourceHandle');
    const edgesByTarget = keyBy(updatedEdges, 'targetHandle');

    // Clear all suggestions that involve the removed node
    if (suggestedEdges.length > 0) {
      const updatedSuggestedEdges = suggestedEdges.filter(edge => edge.source !== nodeId && edge.target !== nodeId);
      set({ suggestedEdges: updatedSuggestedEdges });
    }

    set(state => {
      state.edges = updatedEdges;
      state.nodes = state.nodes.filter(node => node.id !== nodeId);
      state.edgesBySource = edgesBySource;
      state.edgesByTarget = edgesByTarget;
    });

    pathwaysCacheActions.clearNodeCache(id, nodeId);
    caseActions.markUnbalanced();
  },
};

// selectors
export const useNodes = () => usePathwayStore(store => store.nodes);
export const useEdges = () => usePathwayStore(store => store.edges);
export const useSuggestedEdges = () => usePathwayStore(store => store.suggestedEdges);
export const usePathwayId = () => usePathwayStore(store => store.id);
export const useCaseId = () => usePathwayStore(store => store.case_id);
export const usePathwayCustomProperties = () => usePathwayStore(store => store.customProperties ?? []);
export const useNodeCount = () => usePathwayStore(store => store?.nodes?.length);
export const useIsIOConnected = name =>
  usePathwayStore(store => store?.edgesBySource?.[name] || store?.edgesByTarget?.[name]);
export const useIsPathwayLocked = () => usePathwayStore(store => store.locked);
export default usePathwayStore;
export const usePathwayName = () => usePathwayStore(store => store.name);
export const useParentPathwayId = () => usePathwayStore(store => store.parent_pathway_id);
export const useParentCaseId = () => usePathwayStore(store => store.parent_case_id);
export const useParentNodeId = () => usePathwayStore(store => store.parent_node_id);
